import { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../../services/portalApi';

/**
 * Request a password reset link.
 *
 * The confirmation is deliberately the same whether or not the address is
 * registered — matching the server, which always answers 200. Saying "no such
 * account" would turn this page into a way to test which addresses are
 * customers.
 */
export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await authApi.forgotPassword(email.trim());
    } catch {
      /* Same message either way — a network blip must not imply the address
         does or does not exist. */
    } finally {
      setBusy(false);
      setSent(true);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-sm p-8">
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">PatrolIt</h1>
          <p className="text-sm text-gray-500 mt-1">Reset your password</p>
        </div>

        {sent ? (
          <>
            <p className="text-sm text-gray-600">
              If that address belongs to a PatrolIt account, we've sent it a reset link. It
              expires in an hour and can only be used once.
            </p>
            <p className="text-xs text-gray-400 mt-3">
              Nothing arrived? Check spam, and that you used the address you sign in with.
            </p>
            <Link
              to="/login"
              className="mt-6 block text-center px-6 py-2.5 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 transition-colors"
            >
              Back to sign in
            </Link>
          </>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
                placeholder="admin@yoursite.com"
              />
            </div>
            <button
              type="submit"
              disabled={busy || !email.trim()}
              className="w-full py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm transition-colors disabled:opacity-60"
            >
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
            <p className="text-center text-xs text-gray-400">
              <Link to="/login" className="text-brand-600 hover:underline">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
